import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { getConsolidatedCantonsForRegistration } from '../utils/cantons';
import api from '../services/api';
import { useSEO } from '../hooks/useSEO';

const PageContainer = styled.div`
  min-height: ${(p) => (p.$embedded ? 'auto' : '100vh')};
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  padding: ${(p) => (p.$embedded ? '0' : '2rem 0')};
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 3rem;
`;

const Title = styled.h1`
  font-size: 3rem;
  font-weight: 800;
  color: #043663;
  margin-bottom: 1rem;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const Subtitle = styled.p`
  font-size: 1.25rem;
  color: #64748b;
  max-width: 600px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const ActionsBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  gap: 1rem;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const FiltersContainer = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    width: 100%;
    flex-direction: column;
  }
`;

const FilterSelect = styled.select`
  padding: 0.75rem 1rem;
  border: 2px solid #cbd5e1;
  border-radius: 8px;
  font-size: 1rem;
  background: white;
  color: #1f2937;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    border-color: #043663;
  }
  
  &:focus {
    outline: none;
    border-color: #043663;
    box-shadow: 0 0 0 3px rgba(4, 54, 99, 0.1);
  }
`;

const Button = styled.button`
  padding: 0.75rem 2rem;
  background: linear-gradient(135deg, #043663 0%, #0a4d7a 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 15px -3px rgba(4, 54, 99, 0.3);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const PlayersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const PlayerCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
  }
`;

const PlayerName = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  color: #043663;
  margin-bottom: 1rem;
`;

const PlayerInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
`;

const InfoRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #64748b;
  font-size: 1rem;
`;

const InfoLabel = styled.span`
  font-weight: 600;
  color: #1f2937;
  min-width: 100px;
`;

const ContactButton = styled.a`
  display: inline-block;
  width: 100%;
  padding: 0.75rem;
  background: linear-gradient(135deg, #d52b1e 0%, #e6392a 100%);
  color: white;
  text-align: center;
  text-decoration: none;
  border-radius: 8px;
  font-weight: 600;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 15px -3px rgba(213, 43, 30, 0.3);
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  
  @media (max-width: 768px) {
    padding: 10px;
    align-items: flex-start;
    padding-top: 20px;
  }
`;

const Modal = styled.div`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  max-width: 500px;
  width: 100%;
  max-height: calc(100vh - 40px);
  overflow-y: auto;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.3);
  margin: auto;
  -webkit-overflow-scrolling: touch;
  
  @media (max-width: 768px) {
    max-height: calc(100vh - 20px);
    padding: 1.5rem;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const ModalTitle = styled.h2`
  font-size: 1.75rem;
  font-weight: 700;
  color: #043663;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #64748b;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  
  &:hover {
    background: #f1f5f9;
    color: #1f2937;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-weight: 600;
  color: #1f2937;
  font-size: 0.875rem;
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 2px solid #cbd5e1;
  border-radius: 8px;
  font-size: 1rem;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #043663;
    box-shadow: 0 0 0 3px rgba(4, 54, 99, 0.1);
  }
`;

const Select = styled.select`
  padding: 0.75rem;
  border: 2px solid #cbd5e1;
  border-radius: 8px;
  font-size: 1rem;
  background: white;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #043663;
    box-shadow: 0 0 0 3px rgba(4, 54, 99, 0.1);
  }
`;

const ErrorMessage = styled.div`
  color: #d52b1e;
  font-size: 0.875rem;
  margin-top: 0.25rem;
`;

const SubmitButton = styled(Button)`
  width: 100%;
  margin-top: 1rem;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: #64748b;
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: #64748b;
  font-size: 1.25rem;
`;

const SKILL_LEVEL_KEYS = ['1.0-2.5', '3.0-3.5', '4.0-4.5', '5.0-5.5', '6.0-6.5', '7.0-7.5', '8.0-10.0'];

const FindTeammatePage = ({ embedded = false }) => {
  const { t } = useTranslation();

  useSEO(
    embedded
      ? {}
      : {
          title: t('findTeammatePage.seo.title'),
          description: t('findTeammatePage.seo.description'),
          keywords: t('findTeammatePage.seo.keywords'),
          canonical: 'https://helveticpadelseries.ch/find-teammate',
        }
  );

  const skillLevelsMap = t('findTeammatePage.skillLevels', { returnObjects: true });
  const getSkillLevelLabel = (skillLevel) =>
    typeof skillLevelsMap === 'object' && skillLevelsMap !== null && skillLevelsMap[skillLevel]
      ? skillLevelsMap[skillLevel]
      : skillLevel;
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedCanton, setSelectedCanton] = useState('');
  const [selectedSkillLevel, setSelectedSkillLevel] = useState('');
  const [isFeatureEnabled, setIsFeatureEnabled] = useState(true);
  const [checkingFeature, setCheckingFeature] = useState(true);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    canton: '',
    skillLevel: ''
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const cantons = getConsolidatedCantonsForRegistration();

  useEffect(() => {
    checkFeatureEnabled();
  }, []);

  useEffect(() => {
    if (isFeatureEnabled) {
      fetchPlayers();
    }
  }, [selectedCanton, selectedSkillLevel, isFeatureEnabled]);

  const checkFeatureEnabled = async () => {
    try {
      setCheckingFeature(true);
      const response = await api.get('/teammate-finder/feature-enabled');
      if (response.success) {
        setIsFeatureEnabled(response.enabled !== false); // Default to true if not set
      }
    } catch (error) {
      console.error('Error checking feature status:', error);
      // Default to enabled if error
      setIsFeatureEnabled(true);
    } finally {
      setCheckingFeature(false);
    }
  };

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (showModal) {
      // Save current scroll position
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
      
      return () => {
        // Restore scroll position when modal closes
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.overflow = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [showModal]);

  const fetchPlayers = async () => {
    try {
      setLoading(true);
      const params = {};
      
      if (selectedCanton) params.canton = selectedCanton;
      if (selectedSkillLevel) params.skillLevel = selectedSkillLevel;
      
      const response = await api.get('/teammate-finder', { params });
      if (response.success) {
        setPlayers(response.data);
      }
    } catch (error) {
      console.error('Error fetching players:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.firstName.trim()) newErrors.firstName = t('findTeammatePage.errors.firstNameRequired');
    if (!formData.lastName.trim()) newErrors.lastName = t('findTeammatePage.errors.lastNameRequired');
    if (!formData.email.trim()) {
      newErrors.email = t('findTeammatePage.errors.emailRequired');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('findTeammatePage.errors.emailInvalid');
    }
    if (!formData.phoneNumber.trim()) newErrors.phoneNumber = t('findTeammatePage.errors.phoneRequired');
    if (!formData.canton) newErrors.canton = t('findTeammatePage.errors.cantonRequired');
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);
      setSuccessMessage('');
      
      const response = await api.post('/teammate-finder', {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim(),
        phoneNumber: formData.phoneNumber.trim(),
        canton: formData.canton,
        skillLevel: formData.skillLevel || null // Send as string (e.g., "3.0-3.9") or null
      });

      if (response.success) {
        setSuccessMessage(t('findTeammatePage.successPublished'));
        // Reset form
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phoneNumber: '',
          canton: '',
          skillLevel: ''
        });
        // Refresh players list
        setTimeout(() => {
          fetchPlayers();
          setShowModal(false);
          setSuccessMessage('');
        }, 2000);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      const errorMessage = error.response?.data?.errors 
        ? error.response.data.errors.join(', ')
        : error.response?.data?.message || error.message || t('findTeammatePage.errors.submitFailed');
      setErrors({
        submit: errorMessage
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '',
      canton: '',
      skillLevel: ''
    });
    setErrors({});
    setSuccessMessage('');
  };

  // Show loading while checking feature status
  if (checkingFeature) {
    return (
      <PageContainer $embedded={embedded}>
        <Container>
          <div style={{ textAlign: 'center', padding: '4rem 2rem', color: '#64748b' }}>
            {t('findTeammatePage.loading')}
          </div>
        </Container>
      </PageContainer>
    );
  }

  // Show disabled message if feature is disabled
  if (!isFeatureEnabled) {
    return (
      <PageContainer $embedded={embedded}>
        <Container>
          <div style={{ 
            textAlign: 'center', 
            padding: '4rem 2rem',
            background: 'white',
            borderRadius: '16px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            maxWidth: '600px',
            margin: '4rem auto'
          }}>
            <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>ðŸ”’</div>
            <h1 style={{ fontSize: '2rem', fontWeight: 700, color: '#043663', marginBottom: '1rem' }}>
              {t('findTeammatePage.disabled.title')}
            </h1>
            <p style={{ fontSize: '1.125rem', color: '#64748b', lineHeight: '1.6' }}>
              {t('findTeammatePage.disabled.body')}
            </p>
            <p style={{ fontSize: '0.875rem', color: '#94a3b8', marginTop: '1rem', fontStyle: 'italic' }}>
              {t('findTeammatePage.disabled.footnote')}
            </p>
          </div>
        </Container>
      </PageContainer>
    );
  }

  // Legacy anonymous bulletin retired â€” player account public profiles
  return (
    <PageContainer $embedded={embedded}>
      <Container>
        <div
          style={{
            textAlign: 'center',
            padding: embedded ? '1.5rem 1rem' : '4rem 2rem',
            background: 'white',
            borderRadius: '16px',
            boxShadow: embedded ? 'none' : '0 4px 6px rgba(0, 0, 0, 0.1)',
            maxWidth: '640px',
            margin: embedded ? '0' : '4rem auto',
          }}
        >
          <h1 style={{ fontSize: embedded ? '1.35rem' : '2rem', fontWeight: 700, color: '#043663', marginBottom: '1rem' }}>
            {t('findTeammatePage.migration.title')}
          </h1>
          <p style={{ fontSize: '1rem', color: '#64748b', lineHeight: 1.6, marginBottom: '1.25rem' }}>
            {t('findTeammatePage.migration.body')}
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.65rem', alignItems: 'center' }}>
            <Link
              to="/player/teams?tab=find"
              style={{ color: '#043663', fontWeight: 700, fontSize: '0.95rem' }}
            >
              {t('findTeammatePage.migration.ctaBrowse')}
            </Link>
            <Link
              to="/player/settings/profile"
              style={{ color: '#043663', fontWeight: 700, fontSize: '0.95rem' }}
            >
              {t('findTeammatePage.migration.ctaProfile')}
            </Link>
            {!embedded ? (
              <Link to="/login" style={{ color: '#0f766e', fontWeight: 600, fontSize: '0.9rem' }}>
                {t('findTeammatePage.migration.ctaLogin')}
              </Link>
            ) : null}
          </div>
        </div>
      </Container>
    </PageContainer>
  );
};

export default FindTeammatePage;
